import { Component, OnInit } from '@angular/core';
import {
  RegistrationStepService,
  StepType,
  RegistrationChildQuestionsModel,
  RegistrationQuestionType,
  LoggingService,
  ErrorStateService,
  RegistrationAnswerModel,
} from '@fgb/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'fgb-registration-child-questions',
  templateUrl: './registration-child-questions.component.html',
  styleUrls: ['./registration-child-questions.component.scss'],
})
export class RegistrationChildQuestionsComponent implements OnInit {
  readonly stepType = StepType.ChildQuestions;
  readonly questionType = RegistrationQuestionType;
  /** Object holding selected answers of the user. Key format: child__question[__answer] */
  answers: { [id: string]: any } = {};
  children$: Observable<RegistrationChildQuestionsModel[]>;
  private children: RegistrationChildQuestionsModel[];

  constructor(
    private registrationStepService: RegistrationStepService,
    private logService: LoggingService,
    public errorService: ErrorStateService
  ) {}

  ngOnInit() {
    this.children$ = this.registrationStepService.fetchRegistrationStepForCurrentWizard(this.stepType).pipe(
      map(step => (step ? step.Data : undefined)),
      tap(children => (this.children = children)),
      tap(children => this._updateSelectedAnswers(children))
    );
  }

  submitStep() {
    const stepData = this._processAnswers(this.answers);
    if (stepData) {
      this.registrationStepService.submitRegistrationStepData(this.stepType, stepData).toPromise();
    }
  }

  /** Process the answers object into the correct type in preparation for submission to the API. */
  private _processAnswers(answers: { [id: string]: any }): RegistrationChildQuestionsModel[] | null {
    if (!answers || Object.keys(answers).length === 0) {
      this.logService.error('No answers submitted...');
      this.errorService.addError('Please answer the required questions');
      return null;
    }

    let result: RegistrationChildQuestionsModel[] = JSON.parse(JSON.stringify(this.children));

    for (const key in answers) {
      if (answers.hasOwnProperty(key)) {
        const keyParts = key.split('__');

        if (keyParts.length < 2) {
          this.errorService.addError(
            `Encountered invalid key format '${key}' - key should be in the format 'child__question__answer'`
          );
          return null;
        }

        const childId: number = +keyParts[0];
        let child = result.find(x => x.RegistrationID === childId);
        if (!child) {
          this.logService.error('Failed to find child with RegistrationID', childId, 'for answer key', key);
          continue;
        }

        const questionId: number = +keyParts[1];
        let question = child.ClubQuestionAnswer.find(x => x.Question.Id === questionId);
        if (!question) {
          this.logService.error('Failed to find question with Id', questionId, 'for answer key', key);
          continue;
        }

        const answerId: number = keyParts.length > 2 ? +keyParts[2] : NaN;
        const givenAnswer = answers[key];

        let answer: RegistrationAnswerModel | undefined;

        // Update the matching question to have the correct answer specified
        switch (question.Question.QuestionType) {
          case RegistrationQuestionType.TextBox:
            question.Answer[0].Answer = givenAnswer;
            break;

          case RegistrationQuestionType.MultiSelect:
            if (!answerId) {
              this.logService.error('No answer ID provided in key for multiselect question', questionId, 'with answer key', key);
              continue;
            }

            answer = question.Answer.find(x => x.Id === answerId);
            if (!answer) {
              this.logService.error('Failed to find answer with Id', givenAnswer, 'for answer key', key);
              continue;
            }

            answer.Selected = givenAnswer;
            break;

          case RegistrationQuestionType.DropDown:
          case RegistrationQuestionType.Radio:
            question.Answer.forEach(x => x.Selected = false);

            answer = question.Answer.find(x => x.Id === +givenAnswer);
            if (!answer) {
              if (+givenAnswer) {
                this.logService.error('Failed to find answer with Id', givenAnswer, 'for answer key', key);
              }
              continue;
            }

            answer.Selected = true;
            break;
        }
      }
    }

    return result;
  }

  /** Convert a list of RegistrationChildQuestionModel returned from the API into the answers object. */
  private _updateSelectedAnswers(children: RegistrationChildQuestionsModel[]): void {
    children.forEach(child => {
      child.ClubQuestionAnswer.forEach(question => {
        const key = `${child.RegistrationID}__${question.Question.Id}`;
        switch (question.Question.QuestionType) {
          case RegistrationQuestionType.TextBox:
            this.answers[key] = question.Answer[0].Answer;
            break;
          case RegistrationQuestionType.MultiSelect:
            question.Answer.forEach(answer => {
              this.answers[`${key}__${answer.Id}`] = answer.Selected;
            });
            break;
          case RegistrationQuestionType.DropDown:
          case RegistrationQuestionType.Radio:
            this.answers[key] = (question.Answer.find(x => x.Selected) || { Id: 0 }).Id;
            break;
        }
      });
    });
  }
}
